// Adding a boat to the GRP sheet. A GRP boat is a top-level row (Name, SAP, Model, …) with its QA inspection
// tasks as rows underneath (sections, then the points to check). A new boat gets those task rows copied
// from an existing boat of the same kind: boats with "MK-" in their name have their own inspection tasks,
// so an MK- boat copies from the latest MK- boat and any other boat from the latest non-MK boat (the latest
// with a full set: a boat missing sections is never the default model). Only the
// task names and their nesting are copied, never results (status, defects, inspector, dates).
// A boat already on the sheet can take another boat's task rows too (grpMergePlan): only the rows it doesn't
// have yet are added, each where it sits on the other boat, and the rows it has keep their results.
/** most task rows one boat may copy (a guard against picking a whole sheet by mistake). */
export const GRP_TEMPLATE_MAX_ROWS = 400;
/** a boat whose inspection tasks are the MK- set. */
export const isMkBoat = (name) => /mk-/i.test(name);
const text = (v) => (typeof v === 'string' ? v.trim() : '');
/** the task rows under `rootId`, as a tree of names (sheet order). Blank leaf rows are left out; a blank
 *  row that has tasks under it is kept, so the nesting stays as it is. */
export function grpTaskTree(rows, primaryColId, rootId) {
    const kids = new Map();
    for (const r of rows) {
        if (r.parentId == null)
            continue;
        const list = kids.get(r.parentId);
        if (list)
            list.push(r);
        else
            kids.set(r.parentId, [r]);
    }
    const build = (id, depth) => {
        if (depth > 12)
            return []; // a malformed outline can't loop forever
        return (kids.get(id) ?? []).flatMap((r) => {
            const children = build(r.id, depth + 1);
            const name = text(r.cells[String(primaryColId)]);
            return name || children.length ? [{ name, children, id: r.id }] : [];
        });
    };
    return build(rootId, 0);
}
/** how many rows a task tree has. */
export function countTasks(tree) {
    return tree.reduce((n, t) => n + 1 + countTasks(t.children), 0);
}
/** the boats a new boat can copy its tasks from: top-level rows with task rows under them, in sheet order. */
export function grpTemplateBoats(rows, primaryColId) {
    const parents = new Map();
    for (const r of rows)
        if (r.parentId != null)
            parents.set(r.parentId, (parents.get(r.parentId) ?? 0) + 1);
    return rows
        .filter((r) => r.parentId == null && parents.has(r.id))
        .map((r) => {
        const name = text(r.cells[String(primaryColId)]);
        return { id: r.id, name, tasks: countTasks(grpTaskTree(rows, primaryColId, r.id)), mk: isMkBoat(name) };
    })
        .filter((b) => b.tasks > 0);
}
/** the boat a boat named `name` takes its tasks from by default: the latest boat of the same kind (MK- or
 *  not) with a full set, at least 80% as many task rows as the fullest boat of that kind (so one added by
 *  hand, or missing sections, isn't the model), or null when the sheet has none yet. */
export function defaultTemplateBoat(boats, name) {
    const mk = isMkBoat(name);
    const kind = boats.filter((b) => b.mk === mk);
    const full = Math.max(0, ...kind.map((b) => b.tasks)) * 0.8;
    for (let i = kind.length - 1; i >= 0; i--)
        if (kind[i].tasks >= full)
            return kind[i];
    return null;
}
const nameKey = (s) => s.trim().replace(/\s+/g, ' ').toLowerCase();
/** what applying the task rows `tree` to the boat `targetId` adds: at each level, a template row matches a
 *  row the boat already has by name (ignoring case and spacing; each of the boat's rows matched once), and
 *  the rows under it are compared in turn. The template rows with no match are added with everything under
 *  them, in runs, each placed where it sits on the template: below the matched row before it, else above
 *  the matched row after it, else at the end. Nothing the boat has is changed or added twice.
 *  `only` (the template's top-level row ids) limits it to those sections; the others still place them. */
export function grpMergePlan(rows, primaryColId, targetId, tree, only) {
    const kids = new Map();
    for (const r of rows) {
        if (r.parentId == null)
            continue;
        const list = kids.get(r.parentId);
        if (list)
            list.push(r);
        else
            kids.set(r.parentId, [r]);
    }
    const plan = { runs: [], add: 0, existing: 0 };
    const walk = (parentId, nodes, depth) => {
        if (depth > 12 || !nodes.length)
            return;
        const picked = (n) => depth > 0 || !only || (n.id != null && only.has(n.id));
        const have = kids.get(parentId) ?? [];
        const used = new Set();
        const matched = nodes.map((n) => {
            const k = nameKey(n.name);
            const hit = have.find((r) => !used.has(r.id) && nameKey(text(r.cells[String(primaryColId)])) === k);
            if (!hit)
                return null;
            used.add(hit.id);
            return hit.id;
        });
        for (let i = 0; i < nodes.length;) {
            const at = matched[i];
            if (at != null) {
                if (picked(nodes[i])) {
                    plan.existing++;
                    walk(at, nodes[i].children, depth + 1);
                }
                i++;
                continue;
            }
            let j = i;
            while (j < nodes.length && matched[j] == null)
                j++;
            const run = nodes.slice(i, j).filter(picked);
            if (!run.length) {
                i = j;
                continue;
            }
            const before = i > 0 ? matched[i - 1] : null; // a run is as long as it goes: the row before it matched
            const after = j < nodes.length ? matched[j] : null;
            plan.runs.push(before != null ? { parentId, siblingId: before, nodes: run } : after != null ? { parentId, siblingId: after, above: true, nodes: run } : { parentId, nodes: run });
            plan.add += countTasks(run);
            i = j;
        }
    };
    walk(targetId, tree, 0);
    return plan;
}
/** a SAP as the sheets hold it: digits, whether Smartsheet sends it as text ("9100951") or as a number
 *  ("9100951.0"); '' when it isn't one */
export function grpSap(v) {
    const s = typeof v === 'number' ? String(v) : typeof v === 'string' ? v.trim() : '';
    const m = /^(\d{6,})(?:\.0+)?$/.exec(s);
    return m ? m[1] : '';
}
const fold = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
/** the GRP sheet's own name for a model (its MODEL column only takes its dropdown's values): the option that
 *  reads the same once case, spaces and dashes are set aside ("GTX" → "GTx"), else the one option that
 *  contains it or is contained in it ("45" → "45 (3200)", "38" → "GS38"); '' when none or several fit
 *  ("40" could be Alpha 40 or Explorer 40s). */
export function grpModelOption(options, model) {
    const want = fold(model);
    if (!want)
        return '';
    const exact = options.filter((o) => fold(o) === want);
    if (exact.length === 1)
        return exact[0];
    const near = options.filter((o) => { const f = fold(o); return !!f && (f.includes(want) || want.includes(f)); });
    return near.length === 1 ? near[0] : '';
}
//# sourceMappingURL=grpBoat.js.map